import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { LogIn, Menu, Search, UserRound, X } from 'lucide-react';
import axios from 'axios';
import { clearAuth, getStoredUser, setToken } from '../auth';
import { useAuth } from '../contexts/AuthContext';
import { useRequestStatus } from '../contexts/RequestStatusContext';
import { useLanguage } from '../hooks/useLanguage';
import SitePreferences from './SitePreferences';
import AuthAvailabilityNotice from './AuthAvailabilityNotice';

const baseURL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:8080/api'
  : '/api';

function Modal({ title, onClose, children }) {
  useEffect(() => {
    const previous = document.body.style.overflow;
    const onKeyDown = (event) => event.key === 'Escape' && onClose();
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="auth-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-dialog-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="auth-dialog__header">
          <h2 id="auth-dialog-title">{title}</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Закрыть">
            <X size={18} />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function openLegal(documentKey) {
  window.dispatchEvent(new CustomEvent('maxim:open-legal', { detail: documentKey }));
}

function hasElevatedRole(user) {
  const roles = user?.roles || user?.authorities || [];
  return roles.some((role) => {
    const value = typeof role === 'string' ? role : role?.authority || role?.name;
    return ['ROLE_MODERATOR', 'ROLE_ADMIN', 'MODERATOR', 'ADMIN'].includes(value);
  });
}

export default function Header() {
  const { currentUser, setCurrentUser } = useAuth();
  const { showSuccess, showError } = useRequestStatus();
  const { t } = useLanguage();
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [authView, setAuthView] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [consents, setConsents] = useState({ agreement: false, privacy: false, consent: false });

  useEffect(() => setMenuOpen(false), [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const previous = document.body.style.overflow;
    const onKeyDown = (event) => event.key === 'Escape' && setMenuOpen(false);
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  useEffect(() => {
    setMessage('');
    if (!authView) {
      setUsername('');
      setPassword('');
      setEmail('');
      setConsents({ agreement: false, privacy: false, consent: false });
    }
  }, [authView]);

  const navItems = useMemo(() => [
    { to: '/search', label: t('nav.search') },
    { to: '/gallery', label: t('nav.gallery') },
    { to: '/year', label: t('nav.serial') },
    { to: '/articles', label: t('nav.articles') },
    { to: '/about', label: t('nav.about') },
  ], [t]);

  const closeAuth = () => setAuthView(null);

  const handleLogin = async (event) => {
    event.preventDefault();
    setMessage('');
    try {
      const login = username.trim();
      const { data } = await axios.post(`${baseURL}/auth/login`, { username: login, password });
      setToken(data.token, data.role, login);

      const authenticatedUser = getStoredUser();
      if (!authenticatedUser) throw new Error('Invalid authentication response');

      setCurrentUser(authenticatedUser);
      closeAuth();
      showSuccess();
    } catch (error) {
      clearAuth();
      setCurrentUser(null);
      const data = error?.response?.data;
      if (typeof data === 'object' && data?.error === 'ACCOUNT_NOT_CONFIRMED') {
        setMessage(t('auth.notConfirmed'));
      } else if (data === 'Login failed') {
        setMessage(t('auth.invalid'));
      } else {
        setMessage(t('auth.failed'));
      }
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    if (!Object.values(consents).every(Boolean)) {
      setMessage(t('auth.required'));
      return;
    }
    try {
      await axios.post(`${baseURL}/auth/register`, { username, password, email });
      closeAuth();
      showSuccess(t('auth.registerSuccess'));
    } catch (error) {
      const text = error?.response?.data;
      setMessage(typeof text === 'string' ? text : t('common.error'));
      showError(error);
    }
  };

  const handleReset = async (event) => {
    event.preventDefault();
    try {
      await axios.post(`${baseURL}/auth/forgot-password`, { email });
      closeAuth();
      showSuccess(t('auth.resetSuccess'));
    } catch (error) {
      setMessage(t('common.error'));
      showError(error);
    }
  };

  const resendActivation = async () => {
    try {
      await axios.post(`${baseURL}/auth/resend-confirmation`, { username });
      showSuccess(t('auth.registerSuccess'));
    } catch (error) {
      showError(error);
    }
  };

  const handleLogout = () => {
    clearAuth();
    setCurrentUser(null);
    setMenuOpen(false);
  };

  const renderNav = (mobile = false) => (
    <nav className={mobile ? 'mobile-navigation' : 'primary-navigation'} aria-label={t('common.menu')}>
      {navItems.map(({ to, label }) => (
        <NavLink
          key={to}
          to={to}
          className={({ isActive }) => isActive ? 'nav-link is-active' : 'nav-link'}
        >
          {label}
        </NavLink>
      ))}
      {currentUser && <NavLink to="/add" className="nav-link">{t('nav.add')}</NavLink>}
      {hasElevatedRole(currentUser) && (
        <NavLink to="/moderation" className="nav-link">{t('nav.moderation')}</NavLink>
      )}
    </nav>
  );

  return (
    <>
      <header className="site-header">
        <div className="site-header__inner">
          <Link to="/" className="archive-wordmark" aria-label="maximgun — home">
            <span
              className="archive-wordmark__monogram"
              aria-hidden="true"
              style={{
                WebkitMaskImage: `url(${process.env.PUBLIC_URL}/Favicon.svg)`,
                maskImage: `url(${process.env.PUBLIC_URL}/Favicon.svg)`,
              }}
            />
            <span className="archive-wordmark__text">
              <strong>{t('brand.name')}</strong>
              <small>{t('brand.descriptor')}</small>
            </span>
          </Link>

          {renderNav()}

          <div className="site-header__actions">
            <Link to="/search" className="icon-button header-search" aria-label={t('nav.search')}>
              <Search size={18} />
            </Link>
            <SitePreferences />
            {currentUser ? (
              <div className="account-menu">
                <span className="account-name"><UserRound size={15} />{currentUser.username}</span>
                <button type="button" className="text-button" onClick={handleLogout}>{t('auth.signOut')}</button>
              </div>
            ) : (
              <button type="button" className="header-sign-in" onClick={() => setAuthView('login')}>
                <LogIn size={16} />{t('auth.signIn')}
              </button>
            )}
            <button
              type="button"
              className="icon-button mobile-menu-button"
              onClick={() => setMenuOpen(true)}
              aria-expanded={menuOpen}
              aria-label={t('common.menu')}
            >
              <Menu size={21} />
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div className="mobile-menu-backdrop" onMouseDown={() => setMenuOpen(false)}>
          <aside className="mobile-menu" onMouseDown={(event) => event.stopPropagation()}>
            <div className="mobile-menu__top">
              <span className="mobile-menu__title">{t('common.menu')}</span>
              <button type="button" className="icon-button" onClick={() => setMenuOpen(false)} aria-label={t('common.close')}>
                <X size={20} />
              </button>
            </div>
            {renderNav(true)}
            <div className="mobile-menu__footer">
              <SitePreferences align="left" />
              {currentUser ? (
                <button type="button" className="secondary-button" onClick={handleLogout}>{t('auth.signOut')}</button>
              ) : (
                <button type="button" className="primary-button" onClick={() => { setMenuOpen(false); setAuthView('login'); }}>
                  {t('auth.signIn')}
                </button>
              )}
            </div>
          </aside>
        </div>
      )}

      {authView === 'login' && (
        <Modal title={t('auth.title')} onClose={closeAuth}>
          <form className="auth-form" onSubmit={handleLogin}>
            <AuthAvailabilityNotice />
            {message && <p className="form-message form-message--error" role="alert">{message}</p>}
            <label><span>{t('auth.username')}</span><input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" required /></label>
            <label><span>{t('auth.password')}</span><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required /></label>
            <button type="submit" className="primary-button auth-submit">{t('auth.submit')}</button>
            {message === t('auth.notConfirmed') && (
              <button type="button" className="text-button" onClick={resendActivation}>{t('auth.resend')}</button>
            )}
            <div className="auth-links">
              <button type="button" className="text-button" onClick={() => setAuthView('reset')}>{t('auth.forgot')}</button>
              <span>{t('auth.noAccount')} <button type="button" className="text-button" onClick={() => setAuthView('register')}>{t('auth.createAccount')}</button></span>
            </div>
          </form>
        </Modal>
      )}

      {authView === 'reset' && (
        <Modal title={t('auth.resetTitle')} onClose={closeAuth}>
          <form className="auth-form" onSubmit={handleReset}>
            {message && <p className="form-message form-message--error" role="alert">{message}</p>}
            <label><span>{t('auth.email')}</span><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required /></label>
            <button type="submit" className="primary-button auth-submit">{t('auth.send')}</button>
            <button type="button" className="text-button" onClick={() => setAuthView('login')}>{t('common.back')}</button>
          </form>
        </Modal>
      )}

      {authView === 'register' && (
        <Modal title={t('auth.registerTitle')} onClose={closeAuth}>
          <form className="auth-form" onSubmit={handleRegister}>
            <AuthAvailabilityNotice />
            {message && <p className="form-message form-message--error" role="alert">{message}</p>}
            <label><span>{t('auth.newUsername')}</span><input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" required /></label>
            <label><span>{t('auth.email')}</span><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required /></label>
            <label><span>{t('auth.password')}</span><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" required /></label>
            <div className="consent-list">
              {[
                ['agreement', t('auth.agreement')],
                ['privacy', t('auth.privacy')],
                ['consent', t('auth.consent')],
              ].map(([key, label]) => (
                <label className="consent-row" key={key}>
                  <input
                    type="checkbox"
                    checked={consents[key]}
                    onChange={(event) => setConsents((value) => ({ ...value, [key]: event.target.checked }))}
                  />
                  <span>{label}</span>
                  <button type="button" className="text-button" onClick={() => openLegal(key)}>{t('auth.details')}</button>
                </label>
              ))}
            </div>
            <button type="submit" className="primary-button auth-submit">{t('auth.createAccount')}</button>
            <span className="auth-switch">{t('auth.hasAccount')} <button type="button" className="text-button" onClick={() => setAuthView('login')}>{t('auth.signIn')}</button></span>
          </form>
        </Modal>
      )}
    </>
  );
}
